/**
 * Hook to capture failed network requests
 */

import { useEffect, useRef, useCallback } from 'react';
import type { NetworkError } from '../types';

interface UseNetworkCaptureOptions {
  enabled: boolean;
  maxErrors: number;
  redactPatterns: RegExp[];
}

interface UseNetworkCaptureResult {
  errors: NetworkError[];
  clearErrors: () => void;
  addError: (error: NetworkError) => void;
}

// Patterns to always redact from request/response bodies
const SENSITIVE_KEYS = [
  /password/i,
  /token/i,
  /api[_-]?key/i,
  /secret/i,
  /auth/i,
  /bearer/i,
  /cookie/i,
  /session/i,
  /credential/i,
];

function sanitizeBody(body: string | undefined, customPatterns: RegExp[]): string | undefined {
  if (!body) return undefined;

  let sanitized = body;
  const allPatterns = [...SENSITIVE_KEYS, ...customPatterns];

  // Truncate long bodies
  if (sanitized.length > 1000) {
    sanitized = sanitized.substring(0, 1000) + '... [truncated]';
  }

  // Try to parse as JSON and redact sensitive fields
  try {
    const parsed = JSON.parse(body);
    const redacted = redactObject(parsed, allPatterns);
    return JSON.stringify(redacted);
  } catch {
    // Not JSON, just apply pattern matching
    for (const pattern of allPatterns) {
      sanitized = sanitized.replace(pattern, '[REDACTED]');
    }
    return sanitized;
  }
}

function redactObject(obj: unknown, patterns: RegExp[]): unknown {
  if (obj === null || obj === undefined) return obj;

  if (Array.isArray(obj)) {
    return obj.map((item) => redactObject(item, patterns));
  }

  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const isSensitive = patterns.some((pattern) => pattern.test(key));
      result[key] = isSensitive ? '[REDACTED]' : redactObject(value, patterns);
    }
    return result;
  }

  return obj;
}

export function useNetworkCapture({
  enabled,
  maxErrors,
  redactPatterns,
}: UseNetworkCaptureOptions): UseNetworkCaptureResult {
  const errorsRef = useRef<NetworkError[]>([]);
  const originalFetch = useRef<typeof fetch | null>(null);

  const addError = useCallback(
    (error: NetworkError) => {
      errorsRef.current = [error, ...errorsRef.current].slice(0, maxErrors);
    },
    [maxErrors]
  );

  const clearErrors = useCallback(() => {
    errorsRef.current = [];
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Store original fetch (bound to window to avoid "Illegal invocation" error)
    originalFetch.current = window.fetch.bind(window);

    // Override fetch
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const startTime = Date.now();
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      const method = init?.method || 'GET';

      try {
        const response = await originalFetch.current!(input, init);
        const duration = Date.now() - startTime;

        // Only capture errors (4xx, 5xx)
        if (!response.ok) {
          let responseBody: string | undefined;
          try {
            // Clone to avoid consuming the body
            const clone = response.clone();
            responseBody = await clone.text();
          } catch {
            // Ignore body read errors
          }

          const error: NetworkError = {
            url,
            method,
            status: response.status,
            statusText: response.statusText,
            timestamp: new Date().toISOString(),
            duration,
            responseBody: sanitizeBody(responseBody, redactPatterns),
            requestBody: sanitizeBody(
              typeof init?.body === 'string' ? init.body : undefined,
              redactPatterns
            ),
          };

          addError(error);
        }

        return response;
      } catch (fetchError) {
        // Network error (no response)
        const error: NetworkError = {
          url,
          method,
          status: 0,
          statusText: fetchError instanceof Error ? fetchError.message : 'Network Error',
          timestamp: new Date().toISOString(),
          duration: Date.now() - startTime,
        };

        addError(error);
        throw fetchError;
      }
    };

    // Also intercept XMLHttpRequest
    const XHROpen = XMLHttpRequest.prototype.open;
    const XHRSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (
      method: string,
      url: string | URL,
      async: boolean = true,
      username?: string | null,
      password?: string | null
    ) {
      (this as XMLHttpRequest & { _pscMethod: string; _pscUrl: string })._pscMethod = method;
      (this as XMLHttpRequest & { _pscUrl: string })._pscUrl = url.toString();
      return XHROpen.call(this, method, url, async, username, password);
    };

    XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
      const xhr = this as XMLHttpRequest & { _pscMethod: string; _pscUrl: string; _pscStartTime: number };
      xhr._pscStartTime = Date.now();

      this.addEventListener('loadend', function () {
        const duration = Date.now() - xhr._pscStartTime;

        // Only capture errors
        if (this.status >= 400 || this.status === 0) {
          const error: NetworkError = {
            url: xhr._pscUrl,
            method: xhr._pscMethod,
            status: this.status,
            statusText: this.statusText || 'Error',
            timestamp: new Date().toISOString(),
            duration,
            responseBody: sanitizeBody(this.responseText, redactPatterns),
            requestBody: sanitizeBody(
              typeof body === 'string' ? body : undefined,
              redactPatterns
            ),
          };

          addError(error);
        }
      });

      return XHRSend.call(this, body);
    };

    // Cleanup
    return () => {
      if (originalFetch.current) {
        window.fetch = originalFetch.current;
      }
      XMLHttpRequest.prototype.open = XHROpen;
      XMLHttpRequest.prototype.send = XHRSend;
    };
  }, [enabled, addError, redactPatterns]);

  return {
    errors: errorsRef.current,
    clearErrors,
    addError,
  };
}
